import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageEditMode } from '@/context/PageEditModeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Inline-editable podcast text field.
 * Click text while page is in edit mode → type → on blur PUTs
 * /api/podcasts/{id} with the single changed field.
 */
export const InlinePodcastField = ({
  podcast,
  field,
  as = 'span',
  className = '',
  onSaved,
  children,
  ...rest
}) => {
  const { enabled } = usePageEditMode();
  const Tag = as;
  const ref = useRef(null);
  const [saving, setSaving] = useState(false);
  const [localValue, setLocalValue] = useState(podcast?.[field]);

  useEffect(() => {
    setLocalValue(podcast?.[field]);
  }, [podcast, field]);

  const display = localValue == null ? '' : String(localValue);

  useEffect(() => {
    if (enabled && ref.current && ref.current.textContent !== display) {
      ref.current.textContent = display;
    }
  }, [enabled, display]);

  if (!enabled) {
    return <Tag className={className} {...rest}>{display}</Tag>;
  }

  const stop = (e) => e.stopPropagation();

  const commit = async (e) => {
    const text = (e.currentTarget.textContent || '').trim();
    if (text === String(localValue ?? '').trim()) return;
    const prev = localValue;
    setLocalValue(text);
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/podcasts/${podcast.id}`,
        { [field]: text },
        { headers: authHeaders() }
      );
      if (res.data?.success === false) throw new Error('Save failed');
      toast.success('Saved');
      if (onSaved) onSaved({ ...podcast, [field]: text });
    } catch (err) {
      setLocalValue(prev);
      if (ref.current) ref.current.textContent = String(prev ?? '');
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Tag
      ref={ref}
      className={`${className} outline-dashed outline-2 outline-yellow-400/70 outline-offset-2 rounded-sm cursor-text focus:outline-primary focus:outline-solid ${saving ? 'opacity-60' : ''}`}
      contentEditable
      suppressContentEditableWarning
      spellCheck
      onMouseDown={stop}
      onClick={stop}
      onPointerDown={stop}
      onBlur={commit}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      data-edit-path={`podcast.${podcast?.id}.${field}`}
      data-edit-podcast-id={podcast?.id}
      data-edit-field={field}
      {...rest}
    >
      {display}
    </Tag>
  );
};

export default InlinePodcastField;
